import { formatPostalCode } from "../formatPostalCode/formatPostalCode.js";

export type PostcodeComponentSpecificity =
  | "area"
  | "district"
  | "sector"
  | "unit"
  | "unknown";

export const normalizePostcodeComponent = (component: string | null) => {
  const trimmed = (component ?? "")
    .trim()
    .toUpperCase()
    .replaceAll(/\s+/g, " ");

  if (/^[A-Z]{1,2}\d{1,2}[A-Z]?\d[A-Z]{2}$/.test(trimmed)) {
    return `${trimmed.slice(0, -3)} ${trimmed.slice(-3)}`;
  }

  return trimmed;
};

export const expandPostcodeRange = (input: string): string[] => {
  const normalized = input.trim().toUpperCase();
  const match = normalized.match(/^([A-Z]+)(\d+)-(\d+)$/);
  if (!match) return [normalized];

  const prefix = match[1] ?? "";
  const start = Number(match[2]);
  const end = Number(match[3]);
  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [normalized];
  }

  const rangeSize = end - start + 1;
  if (rangeSize > 250) return [normalized];

  return Array.from(
    { length: rangeSize },
    (_, index) => `${prefix}${start + index}`,
  );
};

export const getPostcodeComponentSpecificity = (
  component: string | null,
): PostcodeComponentSpecificity => {
  const normalized = normalizePostcodeComponent(component);

  if (!normalized) return "unknown";

  const formatted = formatPostalCode(normalized, "GB");
  if (formatted.valid) return "unit";

  if (/^[A-Z]{1,2}\d{1,2}[A-Z]? \d$/.test(normalized)) return "sector";
  if (/^[A-Z]{1,2}\d{1,2}[A-Z]?$/.test(normalized)) return "district";
  if (/^[A-Z]{1,2}$/.test(normalized)) return "area";

  return "unknown";
};

export const doesPostcodeComponentCover = (parameters: {
  container: string;
  containee: string;
}) => {
  const container = normalizePostcodeComponent(parameters.container);
  const containee = normalizePostcodeComponent(parameters.containee);

  if (!container || !containee) return false;
  if (container === containee) return true;

  const containerSpec = getPostcodeComponentSpecificity(container);
  const containeeSpec = getPostcodeComponentSpecificity(containee);

  if (containerSpec === "unknown" || containeeSpec === "unknown") return false;

  const specRank: Record<
    Exclude<PostcodeComponentSpecificity, "unknown">,
    number
  > = {
    area: 1,
    district: 2,
    sector: 3,
    unit: 4,
  };

  if (specRank[containerSpec] > specRank[containeeSpec]) return false;

  if (containerSpec === "area") {
    return (
      containee.startsWith(container) &&
      /\d/.test(containee[container.length] ?? "")
    );
  }

  if (containerSpec === "district") {
    return (
      containee.startsWith(container) && containee[container.length] === " "
    );
  }

  if (containerSpec === "sector") {
    return (
      containee.startsWith(container) && containee.length > container.length
    );
  }

  return false;
};

export const doPostcodeComponentsOverlap = (parameters: {
  left: string;
  right: string;
}) =>
  doesPostcodeComponentCover({
    container: parameters.left,
    containee: parameters.right,
  }) ||
  doesPostcodeComponentCover({
    container: parameters.right,
    containee: parameters.left,
  });

export const getCoverageMismatches = (parameters: {
  leftPostcodeComponents: string[];
  rightPostcodeComponents: string[];
}) => {
  const left = [
    ...new Set(
      parameters.leftPostcodeComponents
        .map((c) => normalizePostcodeComponent(c))
        .filter(Boolean),
    ),
  ];
  const right = [
    ...new Set(
      parameters.rightPostcodeComponents
        .map((c) => normalizePostcodeComponent(c))
        .filter(Boolean),
    ),
  ];

  const isCoveredBy = (component: string, containers: string[]) =>
    containers.some((container) =>
      doesPostcodeComponentCover({ container, containee: component }),
    );

  const leftNotCoveredByRight = left.filter((c) => !isCoveredBy(c, right));
  const rightNotCoveredByLeft = right.filter((c) => !isCoveredBy(c, left));

  return {
    leftNotCoveredByRight,
    rightNotCoveredByLeft,
  };
};
